import { UserAgent, createUserAgentString } from "@blacknut/javascript-sdk/dist";
import { logD, logE, logW } from "@blacknut/logging/dist";
export interface ErrorResponse {
  error: { code: string; message: string };
}

export interface AddIceCandidateResponse {
  body: RTCIceCandidate;
}

export interface NegotiationMessage {
  method: "hangup" | "addIceCandidate";
  json_data: ErrorResponse | unknown;
  cmd_id: number;
}
/**
 * ------------------
 * This negotiation make synchronous request-response behaviour for websocket protocol
 * This is a transcription of old http negociation
 * This could be merged with websocket client if no use of synchronous request
 * -----------------
 */
let INSTANCE_ID = 0;

interface WebSocketNegotiationOptions {
  url: string;
  namespace: string;

  onReady: (data: any) => void;
  onError: (e: any) => void;
  onClose: () => void;
  onMessage: (m: NegotiationMessage) => void;

  userAgent?: UserAgent;
}
export class WebSocketNegotiation {
  private socketQueueId = 0;
  private socketQueue = {};
  private ws: WebSocket;
  private onReady: (data: any) => void;
  public readonly id: string;
  private LOGGING_TAG: string;
  private _streamerVersion: number[] = [1, 0, 0]; /*Bobomb*/
  public get streamerVersion(): number[] {
    return this._streamerVersion;
  }

  private _streamingSize: {
    width: number;
    height: number;
    upscaleSmallerVideo: boolean;
  } = {
    width: 1280,
    height: 720,
    upscaleSmallerVideo: false,
  };
  public get streamingSize(): {
    width: number;
    height: number;
    upscaleSmallerVideo: boolean;
  } {
    return this._streamingSize;
  }

  private _userAgent: UserAgent;
  public get userAgent(): UserAgent {
    return this._userAgent;
  }
  public set userAgent(v: UserAgent) {
    this._userAgent = v;
  }

  constructor({
    url,
    namespace,
    onReady,
    onError,
    onClose,
    onMessage,
    userAgent,
  }: WebSocketNegotiationOptions) {
    INSTANCE_ID++;
    this.id = `${INSTANCE_ID}`;
    this.LOGGING_TAG = `Nego#${this.id}`;
    logD(this.LOGGING_TAG, "Create new nego WebSocket with id %o", this.id);
    this.userAgent = userAgent;
    this.onReady = onReady;
    this.ws = new WebSocket(url, namespace);
    this.ws.onmessage = (e) => {
      logD(this.LOGGING_TAG, "Got message %o", e.data);
      let data: NegotiationMessage;
      try {
        data = JSON.parse(e.data);
      } catch (er) {
        logE(this.LOGGING_TAG, "Socket parse error: %o %o", er, e.data);
      }

      // Execute callback if message is a response to a previous command
      if (
        typeof data["cmd_id"] != "undefined" &&
        typeof this.socketQueue["i_" + data["cmd_id"]] == "function"
      ) {
        const execFunc = this.socketQueue["i_" + data["cmd_id"]];
        execFunc(data["json_data"]);
        delete this.socketQueue["i_" + data["cmd_id"]]; // to free up memory.. and it is IMPORTANT
        return;
      }
      // Execute onmessage callback if message is a new message
      else {
        onMessage && onMessage(data);
        return;
      }
    };
    this.ws.onopen = this.onOpen.bind(this);
    // Handle messages
    this.ws.onerror = onError;
    this.ws.onclose = (e: Event) => {
      logD(this.LOGGING_TAG, "Closed: %o", e);
      this.ws = null;
      onclose && onClose();
    };
  }

  private _getConfigTimeout: NodeJS.Timeout | undefined;
  private onOpen() {
    logD(this.LOGGING_TAG, "Opened");

    let data: Record<string, string> = { "user-agent": navigator.userAgent };
    if (this.userAgent) {
      data = {
        ...data,
        "X-Blk-User-Agent": createUserAgentString(this.userAgent),
      };
    }
    this.postData("getConfig", data, this.onGetConfig.bind(this));
    this._getConfigTimeout = setTimeout(() => {
      logW(this.LOGGING_TAG, "getConfig timeout reached !");
      this.getData("getIceServers", this.onReady);
    }, 1000);
  }

  private onGetConfig(data?: {
    version: string;
    videoWidth: number;
    videoHeight: number;
    upscaleSmallerVideo: boolean;
  }) {
    if (this._getConfigTimeout) {
      clearTimeout(this._getConfigTimeout);
      this._getConfigTimeout = undefined;
    }
    if (data) {
      this._streamerVersion = data.version.split(".").map((e) => parseInt(e, 10));
      logD(this.LOGGING_TAG, "Streamer version ", this.streamerVersion.join("."));
      this._streamingSize = {
        width: data.videoWidth,
        height: data.videoHeight,
        upscaleSmallerVideo: data.upscaleSmallerVideo,
      };
    } else {
      logE(this.LOGGING_TAG, "No data returned from getConfig");
    }
    this.getData("getIceServers", this.onReady);
  }

  getData(method: string, onReturnFunction: any) {
    this.postData(method, null, onReturnFunction);
  }

  postData(method, data, onReturnFunction) {
    this.socketQueueId++;
    if (typeof onReturnFunction == "function") {
      // the 'i_' prefix is a good way to force string indices, believe me you'll want that in case your server side doesn't care and mixes both like PHP might do
      this.socketQueue["i_" + this.socketQueueId] = onReturnFunction;
    }
    const jsonData = JSON.stringify({
      cmd_id: this.socketQueueId,
      method: method,
      json_data: data,
    });
    try {
      this.ws && this.ws.readyState === WebSocket.OPEN && this.ws.send(jsonData);
    } catch (e) {
      logE(this.LOGGING_TAG, "Sending failed ... .disconnected failed");
    }
  }
  close() {
    if (this.ws) {
      logD(this.LOGGING_TAG, "Closing nego WS %o", this.id);
      this.ws.onclose = () => {
        logD(this.LOGGING_TAG, "Closed");
      };
      this.ws.onerror = undefined;
      this.ws.onmessage = undefined;
      this.ws.onopen = undefined;
      this.ws.close(1000, "close");
      this.ws = null;
    }
  }
}
